import api from '@/api/index'

const ticketCache = {}
let wechatScriptPromise = null

function isWechat() {
    return /micromessenger/i.test(navigator.userAgent)
}

function ensureWechatScript() {
    if (window.wx) {
        return Promise.resolve()
    }
    if (wechatScriptPromise) {
        return wechatScriptPromise
    }

    wechatScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
        script.async = true
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
    })

    return wechatScriptPromise
}

// 小程序 web-view 仅需要加载 JSSDK 即可使用 wx.miniProgram，无需公众号签名配置。
// 返回 in/out/unknown：只有 getEnv 明确返回 false 才可判定不在小程序 WebView。
export async function getMiniProgramWebViewStatus(onDebug) {
    const debug = (stage, detail) => {
        if (typeof onDebug === 'function') onDebug(stage, detail)
    }
    try {
        debug('jssdk-load-start', { hasWx: Boolean(window.wx) })
        await ensureWechatScript()
        if (!window.wx || !window.wx.miniProgram || typeof window.wx.miniProgram.getEnv !== 'function') {
            debug('jssdk-api-unavailable', {
                hasWx: Boolean(window.wx),
                hasMiniProgram: Boolean(window.wx && window.wx.miniProgram),
                hasGetEnv: Boolean(window.wx && window.wx.miniProgram && window.wx.miniProgram.getEnv),
            })
            return 'unknown'
        }
        return await new Promise(resolve => {
            let settled = false
            const done = value => {
                if (settled) return
                settled = true
                resolve(value)
            }
            window.wx.miniProgram.getEnv(res => {
                const isMiniProgram = Boolean(res && res.miniprogram)
                debug('jssdk-get-env', { isMiniProgram })
                done(isMiniProgram ? 'in' : 'out')
            })
            // 低版本客户端不支持时，不能让用户点击按钮后一直无响应。
            window.setTimeout(() => {
                if (settled) return
                debug('jssdk-get-env-timeout', { timeoutMs: 500 })
                done('unknown')
            }, 500)
        })
    } catch (e) {
        debug('jssdk-get-env-error', { message: e && e.message ? e.message : String(e) })
        return 'unknown'
    }
}

// 保留现有布尔调用方的兼容性；新增业务判断请使用 getMiniProgramWebViewStatus。
export async function isMiniProgramWebView(onDebug) {
    return (await getMiniProgramWebViewStatus(onDebug)) === 'in'
}

export async function initWechatJSSDK(shareData = {}) {
    if (!isWechat()) {
        return
    }
    try {
        await ensureWechatScript()
        let url = window.location.href.split('#')[0];
        url = encodeURIComponent(url)
        let ret = ticketCache[url];
        if(!ret){
            ret = await api.common.getJSAPIticket({url: url}).then(res => res)
            ticketCache[url] = ret;
        }
        let option = ret.data;

        window.wx.config({
            debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
            appId: option.appId || 'wx8f280603dc3cb099', // 必填，公众号的唯一标识
            timestamp: option.timestamp, // 必填，生成签名的时间戳
            nonceStr: option.nonceStr, // 必填，生成签名的随机串
            signature: option.signature,// 必填，签名
            jsApiList: [
                'hideAllNonBaseMenuItem',
                'closeWindow',
                'onMenuShareTimeline',
                'onMenuShareAppMessage',
                'updateAppMessageShareData',
                'updateTimelineShareData',
                'hideMenuItems'
            ] // 必填，需要使用的JS接口列表
        });

        window.wx.ready(function () {
            // config信息验证后会执行ready方法，所有接口调用都必须在config接口获得结果之后，config是一个客户端的异步操作，所以如果需要在页面加载时就调用相关接口，则须把相关接口放在ready函数中调用来确保正确执行。对于用户触发时才调用的接口，则可以直接调用，不需要放在ready函数中。
            setupShareOptions({ ...defaultOptions, ...shareData });
        });

        window.wx.error(function (res) {
            // config信息验证失败会执行error函数，如签名过期导致验证失败，具体错误信息可以打开config的debug模式查看，也可以在返回的res参数中查看，对于SPA可以在这里更新签名。
            console.error('initWechatJSSDK failed --> ', res);
        });
    } catch (e) {
        console.error('initWechatJSSDK failed --> ', e);
    }
}

const defaultOptions = {
    title: 'TOPS优选',
    desc: '专业资产筛选平台，为您筛选优质币种', // 分享描述
    imgUrl: `${window.location.origin}/logo.png` // 分享图标
}

export function setupShareOptions(options = defaultOptions) {

    // “分享给朋友”及“分享到QQ”
    window.wx.updateAppMessageShareData({
        ...options,
        link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
    }, function (res) {
        //这里是回调函数
    });

    // “分享到朋友圈”及“分享到QQ空间”
    window.wx.updateTimelineShareData({
        ...options,
        link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
    }, function (res) {
        //这里是回调函数
    });

    // 发送给朋友
    window.wx.onMenuShareAppMessage && window.wx.onMenuShareAppMessage({
        ...options,
        link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        success: function () {
            //alert('分享成功');
        }
    });

    // 朋友圈
    window.wx.onMenuShareTimeline && window.wx.onMenuShareTimeline({
        title: options.title, // 分享标题
        link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl: options.imgUrl, // 分享图标
        success: function () {
            // 用户点击了分享后执行的回调函数
        }
    });
}
export function checkJsApi(options = defaultOptions) {
    window.wx.checkJsApi({
        jsApiList: ['onMenuShareAppMessage', 'updateAppMessageShareData', 'updateTimelineShareData'], // 需要检测的JS接口列表，所有JS接口列表见附录2,
        success: function (res) {
            console.error('initWechatJSSDK checkJsApi success --> ', res);
            // 发送给朋友
            if (res.checkResult.updateAppMessageShareData) {
                updateAppMessageShareData({
                    ...options,
                    link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
                }, function (res) {
                    //alert('分享成功');
                });
            } else {
                window.wx.onMenuShareAppMessage({
                    ...options,
                    link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
                    success: function () {
                        //alert('分享成功');
                    }
                });
            }

            // 朋友圈
            if (res.checkResult.updateTimelineShareData) {
                updateTimelineShareData({
                    title: options.title, // 分享标题
                    link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
                    imgUrl: options.imgUrl, // 分享图标
                }, function (res) {
                    //alert('分享成功');
                });
            } else {
                window.wx.onMenuShareTimeline({
                    title: options.title, // 分享标题
                    link: window.location.href, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
                    imgUrl: options.imgUrl, // 分享图标
                    success: function () {
                        // 用户点击了分享后执行的回调函数
                    }
                })
            }
        }
    });
}

// “分享给朋友”及“分享到QQ”
export function updateAppMessageShareData(option, cb) {
    window.wx.updateAppMessageShareData({
        title: option.title, // 分享标题
        desc: option.desc, // 分享描述
        link: option.link, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl: option.imgUrl, // 分享图标
    }, function (res) {
        //这里是回调函数
        cb && cb(res);
    });
}
// “分享到朋友圈”及“分享到QQ空间”
export function updateTimelineShareData(option, cb) {
    window.wx.updateTimelineShareData({
        title: option.title, // 分享标题
        link: option.link, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
        imgUrl: option.imgUrl, // 分享图标
    }, function (res) {
        //这里是回调函数
        cb && cb(res);
    });
}
// 拍照或从手机相册中选图接口
export function chooseImage() {
    window.wx.chooseImage({
        count: 1, // 默认9
        sizeType: ['original', 'compressed'], // 可以指定是原图还是压缩图，默认二者都有
        sourceType: ['album', 'camera'], // 可以指定来源是相册还是相机，默认二者都有
        success: function (res) {
            // var localIds = res.localIds; // 返回选定照片的本地ID列表，localId可以作为img标签的src属性显示图片
        }
    });
}
// 预览图片接口
export function previewImage() {
    window.wx.previewImage({
        current: '', // 当前显示图片的http链接
        urls: [] // 需要预览的图片http链接列表
    });
}
// 获取网络状态接口
export function getNetworkType() {
    window.wx.getNetworkType({
        success: function (res) {
            
        }
    });
}
// 使用微信内置地图查看位置接口
export function openLocation() {
    window.wx.openLocation({
        latitude: 0, // 纬度，浮点数，范围为90 ~ -90
        longitude: 0, // 经度，浮点数，范围为180 ~ -180。
        name: '', // 位置名
        address: '', // 地址详情说明
        scale: 1, // 地图缩放级别,整形值,范围从1~28。默认为最大
        infoUrl: '' // 在查看位置界面底部显示的超链接,可点击跳转
    });
}
// 获取地理位置接口
export function getLocation() {
    window.wx.getLocation({
        type: 'wgs84', // 默认为wgs84的gps坐标，如果要返回直接给openLocation用的火星坐标，可传入'gcj02'
        success: function (res) {
            // var latitude = res.latitude; // 纬度，浮点数，范围为90 ~ -90
            // var longitude = res.longitude; // 经度，浮点数，范围为180 ~ -180。
            // var speed = res.speed; // 速度，以米/每秒计
            // var accuracy = res.accuracy; // 位置精度
        }
    });
}
// 调起微信扫一扫接口
export function scanQRCode() {
    window.wx.scanQRCode({
        needResult: 0, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果，
        scanType: ["qrCode", "barCode"], // 可以指定扫二维码还是一维码，默认二者都有
        success: function (res) {
            // var result = res.resultStr; // 当needResult 为 1 时，扫码返回的结果
        }
    });
}
// 发起一个微信支付请求
export function chooseWXPay() {
    window.wx.chooseWXPay({
        timestamp: 0, // 支付签名时间戳，注意微信jssdk中的所有使用timestamp字段均为小写。但最新版的支付后台生成签名使用的timeStamp字段名需大写其中的S字符
        nonceStr: '', // 支付签名随机串，不长于 32 位
        package: '', // 统一支付接口返回的prepay_id参数值，提交格式如：prepay_id=\*\*\*）
        signType: '', // 签名方式，默认为'SHA1'，使用新版支付需传入'MD5'
        paySign: '', // 支付签名
        success: function (res) {
            // 支付成功后的回调函数
        }
    });
}
